import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, TextField,
  Checkbox, FormControlLabel, Paper, Link, CircularProgress, Tooltip
} from '@mui/material';
import { HelpOutlineOutlined } from "@mui/icons-material"
import Turnstile from 'react-turnstile';
import { api } from '../../services/api';
import { useHistory, useLocation } from 'react-router-dom';
import PassGoLogo from "../../assets/PassGoLogo.png";
import { useAuth } from "../../components/context/AuthContext"
import { styled } from '@mui/material/styles';
import { esEmailValido } from "../../utils/email";

const CustomCheckbox = styled('span')(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: '#fff',
  backgroundImage: '#fff',
  '.Mui-focusVisible &': {
    outline: '2px auto #5656ff',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#5656ff',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
  },
}));

const CustomCheckboxChecked = styled(CustomCheckbox)({
  backgroundColor: '#5656ff',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otp, setOtp] = useState('');
  const [trusted, setTrusted] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = params.get("returnTo") || "/"; // fallback por defecto
  const history = useHistory();
  const { login, refreshUser } = useAuth();

  const [isNewUser, setIsNewUser] = useState(false);
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [telefono, setTelefono] = useState('');
  const [welcomeLoading, setWelcomeLoading] = useState(false);

  useEffect(() => {
    let id = localStorage.getItem('deviceId');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('deviceId', id);
    }
    setDeviceId(id);
  }, []);

  const sendOtp = async () => {
    if (!isValidEmail(email)) {
      setEmailError("Ingresá un email válido.");
      return;
    }
    if (!captchaToken) {
      alert("Por favor completá el captcha.");
      return;
    }
    setEmailError('');
    setLoading(true);
    try {
      const auth = await api.auth.Login({ email: email, deviceId: deviceId, captcha: captchaToken });
      if (auth.status == "success") {
        if (auth.data && auth.data.token) {
          localStorage.setItem("token", auth.data.token);
          await refreshUser();
          history.push(returnTo);
        }else{
          setOtpSent(true); 
        }
      } else {
        alert(auth.message);
      }
    } catch (err) {
      console.error("Error al enviar OTP:", err);
      alert("No se pudo enviar el código. Verificá tu conexión o captcha.");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      // verificamos primero
      const verifyResult = await api.auth.Verify({ email, otp, deviceId, trusted });

      if (verifyResult.status === "success" && verifyResult.data.token) {
        // Gguardamos el token asi vemos si es nuevo o no
          localStorage.setItem("token", verifyResult.data.token);
          if (verifyResult.data.isNew) {
            // si es nuevo, muestro el paso 3: la carga de nombre y aplledio
            setIsNewUser(true);
          } else {
            // si hay un token, logea normal, hacemos refresh para que logee todo ok
            await refreshUser();
            history.push(returnTo);
          }
      } else {
        alert("Código incorrecto o vencido");
      }
    } catch (err) {
      console.error("Error al verificar el código:", err);
      alert("Error al verificar el código");
    }
    setLoading(false);
  };


  const handleResendOtp = async (e) => {
    e.preventDefault();
    if (resendLoading || resendTimer > 0) return;

    setResendLoading(true);
    try {
      const auth = await api.auth.Login({ email: email, deviceId: deviceId, captcha: captchaToken });
      if (auth.status === "success") {
        setResendTimer(60);
        setShowCaptcha(false);
      } else if (auth.message && auth.message.toLowerCase().includes("captcha")) {
        setShowCaptcha(true);
        setResendLoading(false);
        return;
      } else {
        alert(auth.message);
      }
    } catch (err) {
      alert("No se pudo reenviar el código. Es posible que debas recargar la página y completar el captcha nuevamente.");
    }
    setResendLoading(false);
  };

  const handleWelcomeSubmit = async (e) => {
    e.preventDefault();
    if (!nombre || !apellido) {
      alert("Nombre y apellido son obligatorios");
      return;
    }

    setWelcomeLoading(true);
    try {
      const res = await api.usuarios.EditUsuario({ nombre, apellido, telefono });
      if (res.status === "success") {
        // refrescamos el usuario en el contexto
        await refreshUser();

        // delay asi enviamos a login correctamente
        setTimeout(() => {
          history.push(returnTo);
        }, 300);
      } else {
        alert("No se pudo guardar la información");
      }
    } catch (err) {
      console.error("Error al guardar información:", err);
      alert("No se pudo guardar la información: " + (err.message || "Error desconocido"));
    }
    setWelcomeLoading(false);
  };

  // cuando el captcha es completado, se hace el reenvio
  useEffect(() => {
    if (showCaptcha && captchaToken && !resendLoading && resendTimer === 0) {
      (async () => {
        setResendLoading(true);
        try {
          const auth = await api.auth.Login({ email: email, deviceId: deviceId, captcha: captchaToken });
          if (auth.status === "success") {
            setResendTimer(60);
            setShowCaptcha(false);
          } else if (auth.message && auth.message.toLowerCase().includes("captcha")) {
            setShowCaptcha(true);
          } else {
            alert(auth.message);
          }
        } catch (err) {
          alert("No se pudo reenviar el código. Es posible que debas recargar la página y completar el captcha nuevamente.");
        }
        setResendLoading(false);
      })();
    }
  }, [captchaToken, showCaptcha]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const isValidEmail = esEmailValido;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh"
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url("https://passgo.com.ar/api/static/Fondoregistro.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Paper sx={{ padding: 4, maxWidth: 400, width: '90%', borderRadius: 2 }}>
        <Box textAlign="center" mb={2}>
          <img src={`${PassGoLogo}`} alt="PassGo" style={{ width: "50%", height: "50%" }} />
        </Box>

        {isNewUser ? (
          // si es usuario nuevo, damos la bienvenida y ponemos el form
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0b0062", textAlign: "center" }}>
              ¡Bienvenido/a!
            </Typography>
            <Typography sx={{ mb: 2, color: "#0b0062", textAlign: "center" }}>
              Completá tu información para continuar:
            </Typography>
            <TextField
              label="Nombre"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              required
              fullWidth
              sx={{
                mb: 2,
                '& label.Mui-focused': { color: '#5656FF' },
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#5656ff' },
                  '&.Mui-focused fieldset': { borderColor: '#5656ff' },
                },
              }}
            />
            <TextField
              label="Apellido"
              value={apellido}
              onChange={e => setApellido(e.target.value)}
              required
              fullWidth
              sx={{
                mb: 2,
                '& label.Mui-focused': { color: '#5656FF' },
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#5656ff' },
                  '&.Mui-focused fieldset': { borderColor: '#5656ff' },
                },
              }}
            />
            <TextField
              label="Teléfono (opcional)"
              value={telefono}
              onChange={e => setTelefono(e.target.value)}
              fullWidth
              sx={{
                mb: 2,
                '& label.Mui-focused': { color: '#5656FF' },
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#5656ff' },
                  '&.Mui-focused fieldset': { borderColor: '#5656ff' },
                },
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleWelcomeSubmit}
              disabled={welcomeLoading || !nombre || !apellido}
              sx={{
                backgroundColor: "#5656FF",
                color: "white",
                fontFamily: "Bricolage Grotesque",
                '&:hover': { backgroundColor: '#4444dd' },
              }}
              startIcon={welcomeLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {welcomeLoading ? "Guardando..." : "Continuar"}
            </Button>
          </Box>
        ) : (
          // flujo del login normal. paso 1, EMAIL
          !otpSent ? (
            <>
              <Typography sx={{ color: "#0b0062", fontSize: "18px", textAlign: "center", fontWeight: "600" }} gutterBottom>
                Ingresá tu dirección de correo electrónico.
              </Typography>
              <Typography sx={{ color: "#0b0062", fontSize: "16px", textAlign: "center", fontWeight: "500", mb: 2 }} >
                Te vamos a enviar un código para confirmar tu identidad.
              </Typography>
              <Typography sx={{ color: "#0b0062", fontSize: "14px", textAlign: "center", fontWeight: "500", mb: 1 }} >
                <strong>Importante:</strong> usá una cuenta de correo electrónico que tengas activa y revises con frecuencia.
              </Typography>
              <TextField
                sx={{
                  fontFamily: "Bricolage Grotesque",
                  mb: 2,
                  '& .MuiInputBase-input-MuiOutlinedInput-input': {
                    fontFamily: "Bricolage Grotesque",
                  },
                  '& label.Mui-focused': {
                    color: '#5656FF',
                    fontFamily: "Bricolage Grotesque"
                  },
                  '& .MuiOutlinedInput-root': {
                    height: "50px",
                    '& fieldset': {
                      borderColor: '#B2BAC2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5656ff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5656ff',
                    },
                  }
                }}
                variant="outlined"
                fullWidth
                label="Correo electrónico"
                type="email"
                margin="dense"
                error={!!emailError}
                helperText={emailError}
                value={email}
                disabled={loading}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                }}
              />
              
                <Box my={1} display="flex" justifyContent="center">
                <Turnstile
                  sitekey="0x4AAAAAABk2OLtGz0UBkzfJ"
                  onVerify={(token) => setCaptchaToken(token)}
                />
              </Box>
               
              <Button disabled={loading || !isValidEmail(email) || !captchaToken} fullWidth variant="contained" size="large" sx={{
                mb: 2,
                backgroundColor: "#5656FF",
                color: "white",
                padding: "5px 10px",
                fontFamily: "Bricolage Grotesque",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                width: "100%"
              }} onClick={sendOtp}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                {loading ? 'Enviando...' : 'Enviar código'}
              </Button>
            </>
          ) : (
            //paso 2 CODIGO
            <>
              <Typography sx={{ color: "#0b0062", fontSize: "18px", textAlign: "center", fontWeight: "600" }} gutterBottom>
                Busca el código en tu correo electrónico.
              </Typography>
              <Typography sx={{ color: "#0b0062", fontSize: "16px", textAlign: "center", fontWeight: "500", mb: 2 }}>
                Te enviamos un codigo a <strong>{email}</strong>. Ingresalo acá para continuar
              </Typography>
              <Typography sx={{ color: "#0b0062", fontSize: "14px", textAlign: "center", fontWeight: "600", mb: 2 }}>
                <strong>¿No te llegó el mail?</strong> Revisá tu carpeta de spam
              </Typography>
              <TextField
                sx={{
                  fontFamily: "Bricolage Grotesque",
                  mb: 2,
                  '& .MuiInputBase-input-MuiOutlinedInput-input': {
                    fontFamily: "Bricolage Grotesque",
                  },
                  '& label.Mui-focused': {
                    color: '#5656FF',
                    fontFamily: "Bricolage Grotesque"
                  },
                  '& .MuiOutlinedInput-root': {
                    height: "50px",
                    '& fieldset': {
                      borderColor: '#B2BAC2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5656ff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5656ff',
                    },
                  }
                }}
                fullWidth
                label="Código"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                margin="dense"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CustomCheckbox />}
                    checkedIcon={<CustomCheckboxChecked />}
                    checked={trusted}
                    onChange={(e) => setTrusted(e.target.checked)}
                    inputProps={{ 'aria-label': 'Confiar en este dispositivo' }}
                  />
                }
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: "14px", fontFamily: "Bricolage Grotesque" }}>
                    Confiar en este dispositivo
                    <span
                      onClick={e => e.stopPropagation()}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Tooltip title="Si marcás esta opción, tu sesión se mantendrá iniciada por más tiempo en este dispositivo.">
                        <HelpOutlineOutlined fontSize="small" color="action" style={{ marginLeft: 2, cursor: 'pointer' }} />
                      </Tooltip>
                    </span>
                  </span>
                }
              />
              <Button fullWidth variant="contained" sx={{
                mb: 2,
                backgroundColor: "#5656FF",
                color: "white",
                padding: "5px 10px",
                fontFamily: "Bricolage Grotesque",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                width: "100%"
              }} onClick={verifyOtp}
                disabled={loading || !otp}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Ingresando...' : 'Iniciar sesión'}
              </Button>
              <Link
                href="#"
                underline="hover"
                onClick={handleResendOtp}
                sx={{ my: 2, display: "flex", justifyContent: "center", color: "#5656ff", alignItems: "center" }}
                disabled={resendLoading || resendTimer > 0}
              >
                {resendLoading && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
                {resendLoading
                  ? "Enviando..."
                  : resendTimer > 0
                    ? `Reenviar código (${resendTimer}s)`
                    : "Reenviar código"}
              </Link>
              {showCaptcha && (
                <Box my={1} display="flex" justifyContent="center">
                  <Turnstile
                    sitekey="0x4AAAAAABk2OLtGz0UBkzfJ"
                    onVerify={(token) => setCaptchaToken(token)}
                  />
                </Box>
              )}
            </>
          )
        )}

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", fontFamily: "Bricolage Grotesque" }}>
            Al iniciar sesión, aceptás los{' '}
            <Link href="https://passgo.com.ar/legal" underline="hover">
              Términos de servicio
            </Link>{' '}
            y la{' '}
            <Link href="https://passgo.com.ar/legal" underline="hover">
              Política de privacidad
            </Link>{' '}
            de PassGo.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
